import React from 'react'

import clsx from 'clsx'

import Grid from 'components/Grid'
import Review from 'components/Review'
import Screenshots from 'components/Screenshots'
import Typography from 'components/Typography'
import { EventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import CardHeader from './components/CardHeader'
import PricingSection from './components/PricingSection'
import ProsAndConsSection from './components/ProsAndConsSection'
import FeatureWithRating from '../BestProductCard/components/KeyFeatures/components/FeatureWithRating'

import classes from './BestProductCardDetailed.module.scss'

/** @type {import("./types").default} */
const BestProductCardDetailed = ({
  listing,
  facet,
  onShowPricingModal,
  hasDemoForm,
  categoryName,
  position,
  ...props
}) => {
  const { down } = useViewport()

  const snippets = listing.feature?.snippets?.sort((a, b) => b.text.length - a.text.length)
  const validKeyFeaturesCount = listing.key_features?.filter(feature => feature.global_feature_id !== null).length

  const validFeaturesWithRating = listing.key_features?.filter(
    feature => feature.global_feature_id !== null && feature.avg_rating !== null
  )

  const averageKeyFeatureRating =
    validFeaturesWithRating?.reduce((sum, feature) => sum + feature.avg_rating?.toFixed(1), 0) /
      validFeaturesWithRating.length || 0

  return (
    <EventContext
      value={{
        engagementComponent: 'best-product_card-item',
      }}
    >
      <Grid container className={clsx('BestProductCard', classes.root)} {...props}>
        <Grid item xs={12}>
          <CardHeader
            listing={listing}
            listingPosition={position}
            hasDemoForm={hasDemoForm}
            onShowPricingModal={onShowPricingModal}
          />
        </Grid>
        <Grid container spacing={4} className={classes.content}>
          {!!listing.screenshots?.length && (
            <Grid item xs={12} className={classes.screenshotsContainer}>
              <Screenshots
                screenshots={listing.screenshots}
                variant="horizontal"
                forceArrows
                forceMainImage
                className={classes.screenshots}
                mediaClassName={classes.media}
                mainImageClassName={classes.mainImage}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="heading-md" className={classes.subtitle}>
              User insights about the {facet.label.toLowerCase()} feature
            </Typography>
            {listing?.feature && listing.feature?.summary}
            <Grid container spacing={down('md') ? 3 : 6} className={classes.insights}>
              {snippets.map(snippet => (
                <Grid item xs={12} lg={6} key={snippet.id} className={classes.snippetContainer}>
                  <Review.Snippet size="large" type={snippet.type} snippet={snippet} className={classes.snippet} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="heading-md" className={classes.subtitle}>
              {categoryName} key features coverage
            </Typography>
            <Typography>
              {listing.name} offers {validKeyFeaturesCount} out of the {listing.key_features.length} key features for{' '}
              {categoryName} software identified by reviewers
              {averageKeyFeatureRating < 3.8
                ? ':'
                : `, and its performance is highly rated by users with an average of ${averageKeyFeatureRating.toFixed(
                    1
                  )} out of 5 stars:`}
            </Typography>
            <Grid container spacing={down('md') ? 1 : 1} className={classes.featuresList}>
              {listing.key_features.map((feature, index) => (
                <Grid
                  item
                  xs={12}
                  lg={6}
                  key={`${feature.global_feature_id}_${index}`}
                  className={classes.snippetContainer}
                >
                  <FeatureWithRating feature={feature} className={classes.featureWithRating} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ProsAndConsSection listing={listing} />
          </Grid>
          <Grid item xs={12}>
            <PricingSection listing={listing} onShowPricingModal={onShowPricingModal} hasDemoForm={hasDemoForm} />
          </Grid>
        </Grid>
      </Grid>
    </EventContext>
  )
}

export default BestProductCardDetailed
